import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatUSD } from "@/lib/api";
import { EquipmentCard } from "@/components/EquipmentCard";
import { ArrowRight, Wrench, ShieldCheck, Truck, BadgeDollarSign, MapPin, Check } from "lucide-react";

const HERO_BG = "https://images.pexels.com/photos/4040619/pexels-photo-4040619.jpeg";
const TEXAS_IMG = "https://images.pexels.com/photos/34410302/pexels-photo-34410302.jpeg";

const BRANDS = ["Caterpillar", "John Deere", "Komatsu", "Volvo", "Bobcat", "Case", "JCB", "Grove", "Peterbilt"];

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [recentlySold, setRecentlySold] = useState([]);
  const [stats, setStats] = useState({ inventory_count: 0, brand_count: 0, category_count: 0 });

  useEffect(() => {
    api.get("/equipment", { params: { featured: true, limit: 6 } }).then((r) => setFeatured(r.data));
    api.get("/equipment/recently-sold", { params: { limit: 6 } }).then((r) => setRecentlySold(r.data));
    api.get("/stats").then((r) => setStats(r.data));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0b] via-[#0a0a0b]/80 to-[#0a0a0b]/20" />
        <div className="tx-grain absolute inset-0 opacity-40" />

        <div className="relative mx-auto grid max-w-[1400px] gap-10 px-6 py-24 lg:grid-cols-12 lg:px-10 lg:py-32">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-[#FFB800]">
              <span className="h-[2px] w-10 bg-[#FFB800]" />
              Texas / est. 1987
            </div>
            <h1
              data-testid="hero-headline"
              className="mt-8 font-display text-5xl font-black uppercase leading-[0.9] text-white sm:text-6xl lg:text-[7.5rem]"
            >
              Heavy Iron.
              <br />
              <span className="text-[#FFB800]">Lone Star</span> Tough.
            </h1>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">
              Texas-based dealer of new, used, and refurbished heavy construction equipment. Excavators, dozers, loaders, cranes — all inspected, all ready to work. We ship to any part of the United States.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/inventory"
                data-testid="hero-cta-inventory"
                className="group flex items-center gap-3 bg-[#FFB800] px-8 py-4 font-display text-sm font-bold uppercase tracking-widest text-black transition-colors hover:bg-[#E5A600]"
              >
                View Inventory
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-contact"
                className="border border-white/20 px-8 py-4 font-display text-sm font-bold uppercase tracking-widest text-white transition-colors hover:border-[#FFB800] hover:text-[#FFB800]"
              >
                Talk to a Dealer
              </Link>
            </div>
          </div>

          <div className="lg:col-span-4 flex flex-col justify-end gap-4">
            <div className="border border-[#27272A] bg-[#141415] p-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Live Inventory</div>
              <div className="mt-2 font-display text-5xl font-black text-[#FFB800]">
                {stats.inventory_count || featured.length}
              </div>
              <div className="mt-1 font-display text-sm uppercase tracking-wider text-white/70">Machines Available</div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="border border-[#27272A] bg-[#141415] p-5">
                <div className="font-display text-3xl font-black text-white">{stats.brand_count || BRANDS.length}+</div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">Brands</div>
              </div>
              <div className="border border-[#27272A] bg-[#141415] p-5">
                <div className="font-display text-3xl font-black text-white">38</div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">Years in TX</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BRAND STRIP */}
      <section className="border-b border-white/10">
        <div className="marquee py-8">
          <div className="marquee__track font-display text-2xl font-bold uppercase tracking-widest text-white/30">
            {[...BRANDS, ...BRANDS].map((b, i) => (
              <span key={i} className="flex items-center gap-10">
                {b} <span className="text-[#FFB800]">◆</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
        <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-end">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFB800]">Why Texas Iron</div>
            <h2 className="mt-4 font-display text-4xl font-black uppercase leading-none text-white sm:text-5xl lg:text-6xl">
              Built for the work,<br />not the showroom.
            </h2>
          </div>
          <p className="max-w-md text-base text-white/60">
            Every machine on this lot is put through a 120-point inspection and fluid analysis. If it rolls out of our yard, it rolls straight to the jobsite.
          </p>
        </div>

        <div className="mt-16 grid gap-px border border-[#27272A] bg-[#27272A] lg:grid-cols-4">
          {[
            { icon: ShieldCheck, title: "120-Point Inspection", body: "Every unit vetted front to back by factory-trained techs." },
            { icon: Wrench, title: "Dealer Warranty", body: "30-day powertrain warranty included on every used machine." },
            { icon: Truck, title: "50-State Delivery", body: "Flatbed, lowboy and RGN carriers ship to all 50 states — Texas rates, nationwide reach." },
            { icon: BadgeDollarSign, title: "Financing Available", body: "Flexible terms and rental-purchase options from 12 to 72 months." },
          ].map((f, i) => (
            <div key={i} className="flex flex-col gap-4 bg-[#0a0a0b] p-8">
              <f.icon className="h-10 w-10 text-[#FFB800]" strokeWidth={1.5} />
              <h3 className="font-display text-xl font-bold uppercase text-white">{f.title}</h3>
              <p className="text-sm text-white/60">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="border-y border-white/10 bg-[#0a0a0b]">
        <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
          <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-end">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFB800]">Featured Machines</div>
              <h2 className="mt-4 font-display text-4xl font-black uppercase text-white sm:text-5xl">
                This week on the lot
              </h2>
            </div>
            <Link
              to="/inventory"
              data-testid="home-see-all"
              className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-widest text-[#FFB800] hover:text-white"
            >
              See all inventory <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((item, i) => (
              <EquipmentCard key={item.id} item={item} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* RECENTLY SOLD — social proof */}
      {recentlySold.length > 0 && (
        <section data-testid="recently-sold-section" className="border-b border-white/10 bg-[#0a0a0b]">
          <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
            <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-end">
              <div>
                <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-[#FFB800]">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#FFB800] opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-[#FFB800]" />
                  </span>
                  Recently Sold
                </div>
                <h2 className="mt-4 font-display text-4xl font-black uppercase leading-none text-white sm:text-5xl">
                  Iron that left the yard
                </h2>
                <p className="mt-3 max-w-xl text-sm text-white/60">
                  A live sampling of machines moved in the last 30 days. If you see something similar in our inventory — it won't stay long.
                </p>
              </div>
              <div className="flex gap-px bg-[#27272A]">
                <div className="bg-[#0a0a0b] px-6 py-4 text-center">
                  <div className="font-display text-3xl font-black text-[#FFB800]">{recentlySold.length}</div>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">This Month</div>
                </div>
                <div className="bg-[#0a0a0b] px-6 py-4 text-center">
                  <div className="font-display text-3xl font-black text-white">
                    {formatUSD(recentlySold.reduce((s, x) => s + (x.price || 0), 0))}
                  </div>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">Total Volume</div>
                </div>
              </div>
            </div>

            <div className="mt-12 grid gap-px border border-[#27272A] bg-[#27272A] sm:grid-cols-2 lg:grid-cols-3">
              {recentlySold.map((item, i) => (
                <SoldCard key={item.id} item={item} index={i} />
              ))}
            </div>

            <div className="mt-10 flex items-center justify-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-white/50">
              <Check className="h-4 w-4 text-[#FFB800]" />
              Every machine delivered with title, keys, and a 30-day powertrain warranty
            </div>
          </div>
        </section>
      )}

      {/* TEXAS BAND */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: `url(${TEXAS_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-[#0a0a0b]/70 to-transparent" />
        <div className="relative mx-auto max-w-[1400px] px-6 py-24 text-center lg:px-10 lg:py-32">
          <div className="mx-auto font-mono text-xs uppercase tracking-[0.3em] text-[#FFB800]">
            Rooted in Texas · Shipping nationwide
          </div>
          <h2 className="mx-auto mt-6 max-w-4xl font-display text-4xl font-black uppercase leading-none text-white sm:text-6xl lg:text-7xl">
            From Amarillo to Anchorage,<br />we deliver the iron.
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-base text-white/70">
            Built on Texas grit, now shipping to all 50 states. Flatbed, lowboy, and RGN carriers on call — usually on the road within 5 business days.
          </p>
          <Link
            to="/contact"
            className="mt-10 inline-flex items-center gap-3 bg-[#FFB800] px-10 py-5 font-display text-sm font-bold uppercase tracking-widest text-black hover:bg-[#E5A600]"
          >
            Request a Quote <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}

// Muted variant of EquipmentCard used on the "Recently Sold" strip. Uses a
// diagonal "SOLD" stamp, grayscale image, and strikethrough price to make the
// social proof unmistakable while nudging the visitor toward live inventory.
const SoldCard = ({ item, index }) => {
  const img = item.images?.[0];
  const days = Math.max(1, Math.floor(Math.random() * 28) + 1);
  return (
    <div
      data-testid={`sold-card-${item.id}`}
      style={{ animationDelay: `${Math.min(index, 10) * 60}ms` }}
      className="fade-up group relative flex flex-col bg-[#0a0a0b] p-5"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={img}
          alt={item.title}
          loading="lazy"
          className="h-full w-full object-cover opacity-50 grayscale transition group-hover:opacity-70"
        />
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="-rotate-12 border-4 border-[#FFB800] bg-black/70 px-6 py-2 font-display text-3xl font-black tracking-widest text-[#FFB800] shadow-lg">
            SOLD
          </div>
        </div>
        <div className="absolute bottom-2 left-2 bg-black/70 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-white">
          {days}d ago
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
        <span>{item.brand}</span>
        <span>{item.category}</span>
      </div>
      <h3 className="mt-2 line-clamp-2 font-display text-lg font-bold uppercase leading-tight text-white/80">
        {item.title}
      </h3>
      <div className="mt-4 flex items-end justify-between">
        <div>
          <div className="font-display text-xl font-black text-white/60 line-through">
            {formatUSD(item.price)}
          </div>
          <div className="mt-1 flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-[#FFB800]">
            <MapPin className="h-3 w-3" /> Shipped to {item.location?.split(",")[0]}
          </div>
        </div>
      </div>
    </div>
  );
};
